import * as turf from '@turf/turf'
import { computed, makeObservable, observable } from 'mobx'
import { LngLat } from 'react-map-gl/mapbox'

import {
  IGeoJson2DGeographicCoordinates,
  ISitemapItemShapeCoordinates,
  SitemapItemShapeType,
} from '~/client/graph'

import { ShapeCoordinates } from '../../../models/SitemapItem'
import { getInteriorAnchor } from '../../../utils/SitemapCalculationHelpers'
import { areObjectArraysEqual, copyObjectArray } from '../../../utils/util'
import {
  computeScreenAlignedBoundingBox,
  translateCoordinates,
} from './shapeCoordinatesUtils'

// The first ring is the outline, or the path of an open polyline; any further ring is a hole.
// Rings are open: a closed shape is closed on emit.
export interface IShapePart {
  rings: IGeoJson2DGeographicCoordinates[][]
}

const toPosition = (c: IGeoJson2DGeographicCoordinates): number[] => [
  c.longitude,
  c.latitude,
]

export function isBeyondOneRingShape(shape: ShapeCoordinates): boolean {
  return (
    shape?.type === SitemapItemShapeType.Polyline &&
    (shape as PolyLineShapeCoordinates).isBeyondOneRing
  )
}

export default class PolyLineShapeCoordinates implements ISitemapItemShapeCoordinates {
  public type: SitemapItemShapeType = SitemapItemShapeType.Polyline
  // Largest part first. Never empty, so the outline below always has somewhere to live.
  @observable public parts: IShapePart[] = []
  @observable public isClosed: boolean = null

  public constructor(parts: IShapePart[], isClosed: boolean) {
    this.parts = parts.length ? parts : [{ rings: [[]] }]
    this.isClosed = isClosed
    makeObservable(this)
  }

  // A prototype accessor, so it is not serialized: the wire carries `parts` for a polyline.
  public get coordinates(): IGeoJson2DGeographicCoordinates[] {
    return this.parts[0].rings[0]
  }

  public set coordinates(ring: IGeoJson2DGeographicCoordinates[]) {
    this.parts[0].rings[0] = ring
  }

  @computed
  public get isMultiPart(): boolean {
    return this.parts.length > 1
  }

  // More than the one ring vertex editing works on: several parts, or a hole.
  @computed
  public get isBeyondOneRing(): boolean {
    return this.isMultiPart || this.parts[0].rings.length > 1
  }

  public getBoundingBoxFeature(
    properties: GeoJSON.GeoJsonProperties,
    bearing: number = 0,
  ): GeoJSON.Feature {
    return {
      type: 'Feature',
      properties,
      geometry: {
        type: 'Polygon',
        coordinates: [this.getScreenAlignedBoundingBox(bearing)],
      },
    }
  }

  private getScreenAlignedBoundingBox(bearing: number): number[][] {
    if (!this.vertexPositions.length) return []
    return computeScreenAlignedBoundingBox(
      this.centerPoint[0],
      this.centerPoint[1],
      this.vertexPositions,
      bearing,
    )
  }

  @computed
  private get vertexPositions(): number[][] {
    return this.parts.flatMap(part =>
      part.rings.flatMap(ring => ring.map(toPosition)),
    )
  }

  @computed
  private get minMaxValues() {
    let minLng = Infinity
    let minLat = Infinity
    let maxLng = -Infinity
    let maxLat = -Infinity
    this.vertexPositions.forEach(([lng, lat]) => {
      minLng = Math.min(minLng, lng)
      minLat = Math.min(minLat, lat)
      maxLng = Math.max(maxLng, lng)
      maxLat = Math.max(maxLat, lat)
    })
    return { minLng, minLat, maxLng, maxLat }
  }

  @computed
  public get centerPoint() {
    const { minLng, minLat, maxLng, maxLat } = this.minMaxValues
    return [minLng + (maxLng - minLng) / 2, minLat + (maxLat - minLat) / 2]
  }

  @computed
  private get partFeatureRings(): number[][][][] {
    return this.parts.map(part =>
      part.rings.map(ring => {
        const positions = ring.map(toPosition)
        return this.isClosed && positions.length
          ? [...positions, positions[0]]
          : positions
      }),
    )
  }

  // Where every part but the first carries its own label; the location's anchor sits on the first.
  @computed
  public get additionalPartAnchors(): number[][] {
    return this.partFeatureRings
      .slice(1)
      .map(rings =>
        getInteriorAnchor(
          this.isClosed
            ? { type: 'Polygon', coordinates: rings }
            : { type: 'LineString', coordinates: rings[0] },
        ),
      )
      .filter(anchor => anchor.every(Number.isFinite))
  }

  public getShapePolygonFeature(
    properties: GeoJSON.GeoJsonProperties,
  ): GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> {
    return {
      type: 'Feature',
      properties,
      geometry: this.isMultiPart
        ? { type: 'MultiPolygon', coordinates: this.partFeatureRings }
        : { type: 'Polygon', coordinates: this.partFeatureRings[0] },
    }
  }

  // Every ring is its own line, so no stroke crosses the ground between two parts.
  public getShapeLineStringFeature(
    properties: GeoJSON.GeoJsonProperties,
  ): GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString> {
    const lines = this.partFeatureRings.flat()
    return {
      type: 'Feature',
      properties,
      geometry:
        lines.length > 1
          ? { type: 'MultiLineString', coordinates: lines }
          : { type: 'LineString', coordinates: lines[0] },
    }
  }

  public moveShapePoint(idx: number, moveCoords: LngLat) {
    this.coordinates[idx] = {
      latitude: moveCoords.lat,
      longitude: moveCoords.lng,
    }
  }

  public translate(latitudeOffset: number, longitudeOffset: number) {
    this.parts = this.parts.map(part => ({
      rings: part.rings.map(ring =>
        translateCoordinates(ring, latitudeOffset, longitudeOffset),
      ),
    }))
  }

  public rotate(startPoint: LngLat, endPoint: LngLat) {
    const normPoint = [this.centerPoint[0], this.centerPoint[1] - 0.01]
    const startAngle = turf.angle(
      [startPoint.lng, startPoint.lat],
      this.centerPoint,
      normPoint,
    )
    const endAngle = turf.angle(
      [endPoint.lng, endPoint.lat],
      this.centerPoint,
      normPoint,
    )

    // Every vertex of every part turns about the one pivot, so the parts keep their places.
    const rotated = turf.transformRotate(
      turf.multiPoint(this.vertexPositions),
      endAngle - startAngle,
      { pivot: this.centerPoint },
    ).geometry.coordinates

    let vertex = 0
    this.parts = this.parts.map(part => ({
      rings: part.rings.map(ring =>
        ring.map(() => {
          const [longitude, latitude] = rotated[vertex++]
          return { latitude, longitude }
        }),
      ),
    }))
  }

  @computed
  public get shapeFeatureCoordinates() {
    return this.partFeatureRings[0][0]
  }

  // Vertex editing reaches the first ring of the first part only, so a shape with more offers
  // no handles.
  @computed
  public get shapeEditorPointCoordinates() {
    return this.isBeyondOneRing ? [] : this.coordinates.map(toPosition)
  }

  @computed
  public get shapeAdditionalEditorPointCoordinates() {
    if (this.isBeyondOneRing) {
      return []
    }
    const res = []
    this.shapeFeatureCoordinates.forEach((c, idx) => {
      if (this.shapeFeatureCoordinates.length !== idx + 1) {
        const lngDif = this.shapeFeatureCoordinates[idx + 1][0] - c[0]
        const latDif = this.shapeFeatureCoordinates[idx + 1][1] - c[1]
        res.push([c[0] + lngDif / 2, c[1] + latDif / 2])
      }
    })
    return res
  }

  public isEqual(other: ShapeCoordinates) {
    if (
      other?.type !== this.type ||
      this.isClosed !== other.isClosed ||
      this.parts.length !== (other as PolyLineShapeCoordinates).parts.length
    ) {
      return false
    }
    const otherParts = (other as PolyLineShapeCoordinates).parts
    return this.parts.every(
      (part, partIdx) =>
        part.rings.length === otherParts[partIdx].rings.length &&
        part.rings.every((ring, ringIdx) =>
          areObjectArraysEqual(ring, otherParts[partIdx].rings[ringIdx]),
        ),
    )
  }

  public copy() {
    return new PolyLineShapeCoordinates(
      this.parts.map(part => ({ rings: part.rings.map(copyObjectArray) })),
      this.isClosed,
    )
  }
}
